/* eslint-disable no-underscore-dangle */
import 'whatwg-fetch';

import React from 'react';
import ReactDOM from 'react-dom';
import _assign from 'lodash/assign';
import get from 'lodash/get';
import queryString from 'query-string';
import bugsnag from '@bugsnag/js';
import bugsnagReact from '@bugsnag/plugin-react';
import * as serviceWorker from './serviceWorker';
import EngagementApp from './components/EngagementApp.react';
import Browser from './util/Browser';
import Payload from './util/Payload';
import Recording from './util/Recording';

const bugsnagClient = bugsnag({
  apiKey: 'f89ac788fefb4bbea3b4b791f54d1441',
  appVersion: "1580826839",
  releaseStage: Browser.getEnvironment(),
});

bugsnagClient.use(bugsnagReact, React);

window.bugsnagClient = bugsnagClient;
Object.assign = _assign;

const domContainerNode = document.getElementById('root');
let state = {};

const enableServerLogs = Browser.shouldSendEngagementLog();
let loaded = false;
let hasInitialized = false;

// make 'serverPayload' gloablly accessible
let serverPayload = '';

// Android back button calls this window.dismiss, so click the close button in the app
window.dismiss = function dismiss(target) {
  const el = document.getElementById('closeButton');
  if (el !== null) {
    el.click();
    window.server.notifySdk('dismissSuccess');
  } else {
    window.server.notifySdk('dismissFail');
  }
};

window.server = {
  sessionId: Browser.isLocal() ? 1 : Math.floor(Math.random() * (99999999 - 1)) + 1,
  device: Browser.isMobile(true),
  lastLog: typeof performance === 'object' ? performance.now() : null,
  moat: null,
  addParams(appendMeta) {
    let metaData = {
      sessionId: this.sessionId,
      device: this.device,
      performance:
        typeof performance === 'object' && typeof performance.now === 'function'
          ? Math.round(performance.now() - this.lastLog)
          : 0,
      focus: document.hasFocus(),
    };
    this.lastLog = performance.now();
    if (typeof appendMeta === 'object') {
      metaData = Object.assign(appendMeta, metaData);
    }
    return JSON.stringify(metaData);
  },
  notifySdk(path, scheme) {
    const newSrc = Browser.generateCustomUrl(path, scheme);
    const payload = { url: newSrc };
    const parsed = queryString.parseUrl(window.location.href);
    const relsNo = get(parsed, 'query.m2m_relsno');
    if (false && get(window, 'M2MWebView.postMessage') && relsNo > 335) {
      // Android >3.35; there's a bug in 3.35, so check for whatever version we fix this in.
      console.warn(`notifySdk M2MWebView.postMessage w/ ${payload.url}`);
      window.M2MWebView.postMessage(JSON.stringify(payload));
    } else if (get(window, 'webkit.messageHandlers.m2mJsHandler')) {
      // iOS after a certain version
      console.warn(`notifySdk m2mJsHandler.postMessage w/ ${payload.url}`);
      window.webkit.messageHandlers.m2mJsHandler.postMessage(payload);
    } else {
      console.warn(`notifySdk iframe w/ ${payload.url}`);
      const ifr = document.createElement('iframe');
      ifr.setAttribute('style', 'width:0;height:0;position:absolute;border:none;z-index:-9999');
      ifr.src = payload.url;
      ifr.onerror = function onerror(e) {
        console.warn(`Error in notifySdk on ${payload.url}`);
        return false;
      };
      ifr.width = 0;
      ifr.height = 0;
      if (get(document, 'body')) {
        document.body.appendChild(ifr);
      } else {
        window.bugsnagClient.notify(new Error('No document body exist, cannot notify sdk'));
      }
    }
  },
  logRequestUrl(url) {
    if (Browser.isUrlMoatPixel(url)) {
      // on some creatives, the E2Image Proxy class (in third_party_impr_pels.js)
      // doesn't work.  For these this serves as a backstop...
      // in newer m2m sdk client versions that support calls into this method
      console.log(`logRequestUrl() - setting moat url to ${url}`);
      this.moat = url;
    }
    window.server.foreachAd(ad => {
      window.server.log({
        tag: 'LOG_REQUEST_URL',
        url,
        inmarketParams: { m2m_impr_id: ad.impression_id },
      });
    });
  },
  log(data) {
    let localData = data;
    try {
      const e2Version = Browser.getParameterValue('v');
      const newData = {
        src: 'index.js',
        e2Version,
        ...localData,
        meta_data: this.addParams(localData.meta_data),
      };
      localData = newData;
    } catch (e) {}

    if (!enableServerLogs || localData === '' || localData === '{}') {
      return;
    }

    function doPost() {
      try {
        try {
          console.log('E2: Logging to server: ', JSON.parse(localData));
        } catch {
          console.log('E2: Logging to server: ', localData);
        }
        const url = `${Browser.getHost(
          window.location.href
        )}/m2m/engagement/log?src=${localData.src || 'app.js'}`;
        const xhr = window.XMLHttpRequest
          ? new XMLHttpRequest()
          : new window.ActiveXObject('Microsoft.XMLHTTP');
        xhr.open('POST', url);
        xhr.onreadystatechange = function() {};
        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.send(JSON.stringify(localData));
      } catch (e) {
        console.error('Error posting e2 log:', e);
      }
    }

    if (!loaded) {
      const k = setInterval(() => {
        if (loaded) {
          doPost();
          clearInterval(k);
        }
      }, 100);
    } else {
      doPost();
    }
  },
  foreachAd(fn) {
    try {
      if (typeof serverPayload.data.actions !== 'undefined') {
        var { actions } = serverPayload.data;
      } else {
        return;
      }
      for (const i in actions) {
        if (typeof actions[i].type !== 'undefined' && actions[i].type !== 'queue_interstitial') {
          continue;
        }
        const { ads } = actions[i];
        for (const j in ads) {
          if (typeof ads[j].impression_id === 'undefined') {
            continue;
          }
          fn(ads[j]);
        }
      }
    } catch (e) {}
  },
  logForImprIds(msg) {
    window.server.foreachAd(ad => {
      window.server.log({
        tag: msg,
        inmarketParams: { m2m_impr_id: ad.impression_id },
      });
    });
  },
};

const windowErrorFunction = function windowErrorFunction(e) {
  const payload = typeof serverPayload !== 'undefined' ? serverPayload : null;
  // we include payload here, because a specific impression may not be at fault...
  // server side, we'll log to all impressions in the payload.
  // it's important that this APP_ERROR be the only APP_ERROR with payload as a property
  let o = { wasErrorEvent: false };
  if (typeof e === 'object' && e instanceof ErrorEvent) {
    o.wasErrorEvent = true;
    for (const p in e) {
      if (typeof e[p] === 'object' || typeof e[p] === 'function') {
        continue;
      }
      o[p] = e[p];
    }
  } else {
    o = e;
  }
  window.server.log({
    tag: 'APP_ERROR',
    error: o,
    payload,
  });
};
window.addEventListener('error', windowErrorFunction);

function didFinishShow() {
  console.warn('didFinishShow');
  window.server.notifySdk('didFinishShow');
}

/**
 * The engagement app should call didFinishLoad once it has finished loading.
 * This function indicates to the client that the app is finished loading.
 */
function didFinishLoad() {
  // client should launch all http requests in a browser from now on.
  console.log(
    'Notifying SDK of didFinishLoad(): loaded all iframes, notifying the SDK to launch requests in browser from now on'
  );

  console.warn('didFinishLoad');
  // previously - window.location.href = Browser.generateCustomUrl("http-default-safari")
  window.server.notifySdk('didFinishLoad');
  loaded = true;

  // for in-hand, let's skip waiting for foregrounding and being ready and what-not, shall we?
  if (Payload.isInHand(serverPayload)) {
    console.log(
      "The payload IS inHand, so we're calling prepareToShow() here under the assumption that the app is foreground and ready."
    );
    window.prepareToShow();
  }
}
/**
 * The engagement app should call didFinishHide once it has been fully removed from view.
 * This function indicates to the client that the app has been hidden.
 *
 * In iOS the OM SDK session is finished in the if-statement handling this callback.
 * Shortly afterward, the iOS SDK will call back in with window.unload().
 * The actual window doesn't get hidden till unload() responds with `m2m://interstitial/didFinishUnload`,
 * therefore at this vector we should install, for iOS only, a 1.5s to allow OM SDK to finish the session.
 *
 * Where and when the Android version stops the OM SDK session is a bit more complicated.
 * It happens in the M2MWebView.unload() method, which calls window.unload() 1.5s later.
 * The notion here was to give IAB enough time to fire off the `sessionFinish` event type.
 *
 */
function didFinishHide(target) {
  console.warn('didFinishHide', target);

  // surveys are set as complete and their own and an update of engagement time should not occur
  const { ads } = serverPayload.data.actions['0'];
  for (const index in ads) {
    const pixel = ads[index].tracking_pixels[0];
    const pixelclose = `${pixel}&pixelclose=1`;
    // https://matt-m2m.inmarket.com/advertiser/pixel?m2m_impr_id=60063&m2m_impr_hash=96226d9c4ef6ba3c50dae6feb187e24560965c5b&m2m_server_rels=20150924&pixelclose=1
    if (typeof pixel !== 'undefined' && Browser.validURL(pixel)) {
      const s = document.createElement('img');
      s.style.cssText = `position: absolute;top: -9999px;left: -9999px;`;
      s.width = 0;
      s.height = 0;
      s.src = pixelclose;
      document.body.appendChild(s);
    }
  }

  // previously - window.location.href = Browser.generateCustomUrl("close")
  let url = 'didFinishHide';
  if (typeof target !== 'undefined') {
    url = `${url}?url=${target}`;
  }
  window.setTimeout(() => {
    window.server.notifySdk(url);
  }, 700);
}

function _validatePayload(payload) {
  const localPayload = payload;
  const actions = get(payload, 'data.actions', []);
  actions.forEach(element => {
    const localElement = element;
    const hasFields = localElement.type === 'fields' && get(localPayload, 'data.fields');
    if (localElement.type === 'queue_interstitial' && typeof localElement.ads !== 'undefined') {
      localElement.ads.forEach(ad => {
        // handle token replacement
        if (typeof ad.tracking_pixels !== 'undefined') {
          ad.tracking_pixels = ad.tracking_pixels.map(pixelElement =>
            pixelElement.replace(
              /%25%25CACHEBUSTER%25%25/g,
              Math.floor(Math.random() * (99999999 - 1)) + 1
            )
          );
        }
      });
    } else if (hasFields) {
      localPayload.data.fields = localElement.data;
    }
  });
  if (get(localPayload, 'fields.url_icon')) {
    localPayload.data.fields.url_icon = `https://s3.amazonaws.com/checkpointsmedia/400/xdpi/${localPayload.data.fields.url_icon}`;
  }

  return localPayload;
}

// //////////////////////////////////////////////////////////////////////////////
/**
 * Lifecycle hooks for the SDK to call.
 */
window.preload = function preload(payload) {
  if (hasInitialized) return false;

  let localPayload = payload;
  console.log('SDK called preload() with params: ', localPayload);

  // log that e2 is loaded
  serverPayload = localPayload;
  window.server.logForImprIds('E2_LOADED');
  if (serverPayload.data) {
    try {
      if (Recording.shouldEnableLogRocket(serverPayload.data)) {
        Recording.enableLogRocket(serverPayload.data);
      }
    } catch (e) {
      console.error('Error', e);
    }
    try {
      if (Recording.shouldEnableFullStory(serverPayload.data)) {
        Recording.enableFullStory(serverPayload.data);
      }
    } catch (e) {
      console.error('Error', e);
    }
    try {
      if (Recording.shouldEnableSessionStack(serverPayload.data)) {
        Recording.enableSessionStack(serverPayload.data);
      }
    } catch (e) {
      console.error('Error', e);
    }
  }

  // clear any preexisting engagement
  ReactDOM.unmountComponentAtNode(domContainerNode);
  // validate payload format
  localPayload = _validatePayload(localPayload);

  state = {
    payload: localPayload,
    didFinishHide,
    didFinishLoad,
    listeners: {},
    on(event, cb) {
      const l = (this.listeners[event] = this.listeners[event] || []);
      l.push(cb);
    },
    event(event, eventPayload) {
      console.log(`Calling Event ${event}`);
      if (this.listeners[event]) {
        console.log('have listeners for event');
        for (const l of this.listeners[event]) {
          l(eventPayload);
        }
      }
    },
    Browser,
  };

  const ErrorBoundary = window.bugsnagClient.getPlugin('react');
  const url = get(localPayload, 'data.actions[0].url');
  let thirdParams = {};
  if (url) {
    const params = queryString.parseUrl(url);
    thirdParams = params.query;
    if (thirdParams.url) {
      thirdParams = {
        ...thirdParams,
        ...queryString.parseUrl(thirdParams.url).query,
      };
    }
  }

  window.bugsnagClient.metaData = {
    localPayload,
    ...get(localPayload, 'data.actions[0]', {}),
    ...thirdParams,
  };

  const renderApp = () => {
    ReactDOM.render(
      <ErrorBoundary>
        <EngagementApp
          {...state}
          payload={localPayload}
          didFinishShow={didFinishShow}
          didFinishLoad={didFinishLoad}
          didFinishHide={didFinishHide}
        />
      </ErrorBoundary>,
      domContainerNode,
      () => {
        window.server.notifySdk('didFinishPreload');
      }
    );
  };

  /**
   * Provides a window.injectOmSdkVerificationScript function for use when omsdk_verify=1
   * in the first action of the payload (the queue_interstitial).
   *
   * NOTE!  UPDATE workbox-config.js when you change versions here or omsdk file structure!
   *
   * @param onAfterLoad A function to run after the omsdk is loaded
   * @returns {boolean} True when the omsdk is supported by the mobile device, false if not
   */
  const injectOmSdk = onAfterLoad => {
    const m2mRelsno = Browser.getParameterValue('m2m_relsno');
    const E2Device = m2mRelsno % 2 == 0 ? 'ios' : 'android';

    const validOS =
      (m2mRelsno >= 317 && E2Device === 'android') || (m2mRelsno >= 300 && E2Device === 'ios');

    if (validOS) {
      let doneWithLoad = false;
      const whenDoneWithLoad = () => {
        if (!doneWithLoad) {
          doneWithLoad = true;
          typeof onAfterLoad !== 'function' || onAfterLoad();
        }
      };

      // it's very important that this script is loaded prior to the m2m://interstitial/didFinishPreload call
      // as the client devices will start the omsdk session in that callback...
      // this is one reason why we render the app after the omsdk javascript load
      if (!document.getElementById('omsdk-server-script')) {
        const omsdkJS = document.createElement('script');
        omsdkJS.id = 'omsdk-server-script';
        omsdkJS.type = 'text/javascript';
        omsdkJS.src = '/js/omsdk-js-1.2.17/Service/omsdk-v1.js';
        omsdkJS.onload = whenDoneWithLoad;
        omsdkJS.onerror = whenDoneWithLoad;
        document.head.appendChild(omsdkJS);
      }

      window.injectOmSdkVerificationScript = function() {
        if (!document.getElementById('omsdk-verification-script')) {
          const omsdkVJS = document.createElement('script');
          omsdkVJS.id = 'omsdk-verification-script';
          omsdkVJS.type = 'text/javascript';
          if (document.location.protocol === 'https:') {
            omsdkVJS.src =
              '/js/omsdk-js-1.2.17/Validation-Script/omid-validation-verification-script-v1-ssl.js';
          } else {
            omsdkVJS.src =
              '/js/omsdk-js-1.2.17/Validation-Script/omid-validation-verification-script-v1.js';
          }
          document.head.appendChild(omsdkVJS);
        }
      };
    }
    return validOS;
  };

  // either inject the omsdk then render
  // or just render the app
  injectOmSdk(renderApp) || renderApp();

  return '';
};

/**
 * Should only be called from within E2 manually:
 * - for non-mobile browsers, or
 * - on a manual payload inject.
 *
 * Invoked by the Android SDK as soon as possible after the `m2m://interstitial/didFinishPreload` call.
 * Invoked by the iOS SDK as soon as possible after the `m2m://interstitial/didFinishLoad` call.
 * It is invoked in both cases only at the point the app is in the foreground or coming into the foreground.
 *
 * Given the deviation, the earliest vector needs to be maintained to prevent session loss.
 *
 * The `m2m://interstitial/didFinishPrepareToShow`, called herein, is effectively a boomerang back
 * into the platform sdk that then triggers the tracking sessions for any platform libraries we
 * integrate with (ie, the IAB OM SDK).
 *
 * For the IAB OM SDK, it is imperative that the Service javascript be loaded prior to the platform sdk
 * calling this method.
 *
 * Within a line or two of the sdk calling this method, the Android sdk notifies publisher M2M listeners
 * that an engagement is being viewed.
 *
 * The iOS platform SDK notifies M2M delegates that an engagement is being viewed in response to a
 * notification that is sent when the M2MViewController is presented.
 *
 * @returns {string}
 */
window.prepareToShow = function prepareToShow() {
  console.log('SDK called prepareToShow()');

  // logging to server
  window.server.logForImprIds('E2_PREPARE_TO_SHOW');

  /**
   * Immediately in response to this call, the both platforms start
   * any tracking sessions that they will start with native libraries.
   * (ie, IAB OM SDK)
   */
  window.server.notifySdk('didFinishPrepareToShow');
  return '';
};

window.logRequestUrl = function logRequestUrl(url) {
  console.warn(`SDK called logRequestUrl(${url})`);
  window.server.logRequestUrl(url);
  // another voodoo moment here for android
  // we need to figure out a way to isolate what exactly causes this
  // to be required... it's likely linked to periodic disappearances of the ad
  const parsed = queryString.parseUrl(window.location.href);
  const relsNo = get(parsed, 'query.m2m_relsno');
  if (relsNo % 2 === 1) {
    window.server.notifySdk('ignoreThis');
  }
  return '';
};

window.show = function show() {
  console.log('SDK called show()');
  // logging to server
  window.server.logForImprIds('E2_SHOW');

  if (!hasInitialized) {
    hasInitialized = true;
    console.log('calling app opened');

    if (get(state, 'event')) {
      state.event('app_opened');
    } else {
      window.bugsnagClient.notify(new Error('No state event to call app_opened existed'));
    }
    if (!document.hasFocus()) {
      window.focus();
    }
  }
  return '';
};

window.unload = function unload() {
  const runUnload = () => {
    console.log('SDK called unload()');
    if (get(state, 'event')) {
      state.event('unload');
    } else {
      window.bugsnagClient.notify(new Error('No state event to call unload existed'));
    }
    if (ReactDOM.unmountComponentAtNode(domContainerNode))
      console.log('successfully unloaded application()');
    else {
      console.log('did not unload application');
    }
    state.listeners = {};
    hasInitialized = false;
    window.server.notifySdk('didFinishUnload');
  };
  if (Browser.isIOS()) {
    /*
     "Note that ending an OMID ad session sends a message to the verification scripts running inside the
     webview supplied by the integration. So that the verification scripts have enough time to handle the
     sessionFinish event, the integration must maintain a strong reference to the webview for at least 1.0
     seconds after ending the session."
     - https://interactiveadvertisingbureau.github.io/Open-Measurement-SDKiOS/#7-stop-the-session
     - https://interactiveadvertisingbureau.github.io/Open-Measurement-SDKAndroid/#8-stop-the-session

     The actual window doesn't get hidden/destroyed till unload() responds with `m2m://interstitial/didFinishUnload`,
     therefore at this vector we should install, for iOS only, a 1.5s to allow OM SDK to finish the session,
     which would have been triggered in `m2m://interstitial/didFinishHide`
     (cond calls [M2M_WebView closeButtonPressed] in iOS, which will then call window.unload())
     X -> didFinishHide(finish om) -> unload() -(our delay here)-> didFinishUnload

     For Android our case is currently covered in the platform code and this above `isIOS` condition should eventually
     include a version check, as it should be covered in the platform there as well.

     This timeout was specifically introduced because we were not consistently seeing the `sessionFinish` event through
     Charles Proxy on iOS from the OM SDK validation script, provided by IAB.
    */
    setTimeout(runUnload, 1500);
  } else {
    runUnload();
  }
  return '';
};

/**
 * If an http(s) request is made by the WebApp but the WebApp is not able
 * to intercept and handle it,
 */
window.willSendRequestWithURL = function willSendRequestWithURL(url) {
  let localUrl = url;
  try {
    console.log('SDK called willSendRequestWithURL()');
  } catch (e) {
    console.log('SDK called willSendRequestWithURL() error', e.message);
  }

  // encode all equals signs
  localUrl = encodeURIComponent(localUrl);

  // for now open all urls in a browser
  window.server.notifySdk(`click?target=browser&url=${localUrl}`);
  return false;
};
// end hooks for the SDK
// //////////////////////////////////////////////////////////////////////////////

// //////////////////////////////////////////////////////////////////////////////
// for testing in Chrome mobile emulator from desktop
let payload = Browser.getParameterValue('payload');
if (!Browser.isMobile() || payload) {
  payload = payload || 'spencer';
  console.log('loading payload', payload);
  fetch(`/demo/${payload}.json`)
    .then(r => r.json())
    .then(res => {
      // M2M-CMS preview, populate fields based on query params.
      if (payload === 'preview') {
        if (!get(res, 'data')) {
          return false;
        }
        // Define ad interstitial.
        if (get(res, 'data.actions[0].ads[0]')) {
          let html = '';
          try {
            html = atob(Browser.getParameterValue('interstitial_html'));
          } catch (e) {}
          res.data.actions[0].ads[0] = {
            impression_id: Math.floor(Math.random() * 20000000),
            url: Browser.getParameterValue('url'),
            tracking_pixels: [],
            click_through_url: Browser.getParameterValue('click_through_url'),
            type: Browser.getParameterValue('type'),
            interstitial_html: html,
            click_action: Browser.getParameterValue('click_action'),
            height: Browser.getParameterValue('height'),
            width: Browser.getParameterValue('width'),
            scrollable: Browser.getParameterValue('scrollable'),
            preview: true,
          };
        }
        // Define ad icon, defaults to Checkpoints.
        if (get(res, 'data.fields.publisher')) {
          res.data.fields.publisher.engagement_app_icon = `https://s3.amazonaws.com/ad.inmarket.com/e2/${Browser.getParameterValue(
            'engagement_app_icon'
          )}`;
        }
      }

      console.log('data from server: ', res);

      // Leaving this in because the PHP backend still uses this `body.type`
      if (res.type && res.type === 'sniff-and-tell') {
        window.preload(res.preload);

        // delay to let preload finish preloading
        setTimeout(window.rangingResults(res.rangingResults), 1500);
      } else {
        window.preload(res);
      }
    })
    .catch(err => {
      console.log('error loading sample data');
      console.log(err);
    });
}

// Override image so we can catch moat pixel fires
if (typeof Proxy === 'function') {
  console.log('Adding Proxy for HTMLImageElement');
  /*
   * Extend native Image function to capture src urls.
   * Capture last MOAT pixel url for logging.
   */
  const E2Image = Image;
  class E2ImageClass {
    constructor(w, h) {
      const e2Image = new E2Image(w, h);
      const moatpixel = function moatpixel(value) {
        return (
          typeof value === 'string' && value.indexOf('moat') > -1 && value.indexOf('pixel.gif') > -1
        );
      };
      const handler = {
        set(obj, prop, value) {
          if (prop === 'src' && moatpixel(value)) {
            window.top.postMessage(
              JSON.stringify({
                action: 'moat_pixel_update',
                m2m_impr_id: 0,
                moatPixel: value,
              }),
              '*'
            );
          }
          return (e2Image[prop] = value);
        },
      };
      const prox = new Proxy(e2Image, handler);
      try {
        prox[Symbol.toStringTag] = 'HTMLImageElement';
      } catch (e) {}
      return prox;
    }
  }
  // eslint-disable-next-line
  Image = E2ImageClass;
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
  console.log('Service worker will be registered.');
} else {
  console.log(
    `Service worker will not be registered.  process.env.NODE_ENV=${process.env.NODE_ENV}`
  );
}
serviceWorker.register({
  onUpdate: registration => {
    console.log('serviceWorker updated! registration:', registration);
  },
  onSuccess: registration => {
    console.log('serviceWorker registered! registration:', registration);
  },
});

function bundleLoaded() {
  const parsed = queryString.parseUrl(window.location.href);
  const relsNo = get(parsed, 'query.m2m_relsno');
  console.log('E2 Bundle Loaded with m2m_reslno', relsNo);
  const sendDidFinishBundleLoad = relsNo % 2 === 0 && relsNo >= 306;
  if (sendDidFinishBundleLoad) {
    window.server.notifySdk('didFinishBundleLoad');
  }
}

bundleLoaded();
